import { CUBE_SIDE_NAMES } from "../../components/Сube/constants";
import { CubeSideName } from "../../components/Сube/types";
import { DtoTestSet, DtoEntry, DtoTestSetScale } from "@/types/dto-types";
import { Category, ValuesPerSide } from "./types";
import { SelectedFilters } from "../useFilters";
import { getAggregatedItemsReport } from "./reports-utils";
import { DEFAULT_BENCHMARK_VALUE } from "../../../../../constants";
import { getBenchmarkValue } from "../../utils";

type ScaleValueKey = `S${number}V`;

export function groupTestSetsByCubeSide(testSets: DtoTestSet[]) {
  return CUBE_SIDE_NAMES.reduce((acc, sideName) => {
    acc[sideName] = testSets.filter(
      testSet => testSet.info.cubeSide === sideName
    );
    return acc;
  }, {} as Record<CubeSideName, DtoTestSet[]>);
}

export function groupEntriesByCubeSide(
  testSetsByCubeSide: Record<CubeSideName, DtoTestSet[]>,
  allEntries: DtoEntry[]
) {
  return CUBE_SIDE_NAMES.reduce((acc, sideName) => {
    const testSetIds = testSetsByCubeSide[sideName].map(testSet => testSet._id);
    const sideEntries = allEntries.filter(entry =>
      testSetIds.includes(entry.testSetId)
    );
    return {
      ...acc,
      [sideName]: sideEntries,
    };
  }, {} as Record<CubeSideName, DtoEntry[]>);
}

function getValueKeysByCondition<T>(
  items: T[],
  condition: (item: T) => boolean,
  getKey: (item: T, index: number) => string
): string[] {
  return items.reduce((acc, item, i) => {
    if (condition(item)) {
      acc.push(getKey(item, i));
    }
    return acc;
  }, [] as string[]);
}

export function getScalesValueKeysByCategory(
  scales: DtoTestSetScale[],
  category: Category
) {
  return getValueKeysByCondition(
    scales,
    scale => scale.category === category,
    (_scale, i) => `S${i + 1}V`
  ) as ScaleValueKey[];
}

export function getScalesByCategory(
  scales: DtoTestSetScale[],
  category: Category
) {
  return scales.filter(scale => scale.category === category);
}

export function normalizeValueToPercentage(value: number) {
  return value * 100;
}

export function getAverageScaleValue(
  scaleValueKeys: ScaleValueKey[],
  entry: DtoEntry
) {
  if (scaleValueKeys.length === 0) {
    return 0;
  }

  const accumulator = scaleValueKeys.reduce(
    (acc, key) => {
      const entryValue = entry[key];

      if (entryValue === null) {
        // If the scale value is null, we skip it from the calculation
        return acc;
      }

      return {
        ...acc,
        value: acc.value + entryValue,
        counter: acc.counter + 1,
      };
    },
    { value: 0, counter: 0 }
  );

  return accumulator.value / accumulator.counter;
}

export function getValuesPerSide(
  side: CubeSideName,
  testSetsByCubeSide: Record<CubeSideName, DtoTestSet[]>,
  entriesGroupedBySide: Record<CubeSideName, DtoEntry[]>,
  currentTestId: string,
  benchmarkTitle?: string
): ValuesPerSide {
  const dataAccumulator: ValuesPerSide = {
    totalCount: 0,
    averagePercentageScore: 0,
    benchmarkPercentageScore: 0,
    implementationQualityPercentage: 0,
    importancePercentage: 0,
    aggregatedReport: null,
    consentItems: [],
    qualityItems: [],
    importanceItems: [],
  };

  const currentTestSet = testSetsByCubeSide[side][0];

  if (currentTestSet) {
    const consentScaleKeys = getScalesValueKeysByCategory(
      currentTestSet.scales,
      Category.Consent
    );
    const qualityScaleKeys = getScalesValueKeysByCategory(
      currentTestSet.scales,
      Category.Quality
    );
    const importanceScaleKeys = getScalesValueKeysByCategory(
      currentTestSet.scales,
      Category.Importance
    );
    const benchmarkValue = getBenchmarkValue(currentTestSet, benchmarkTitle);
    // TODO update benchmark handling when multiple test sets per side are supported
    dataAccumulator.benchmarkPercentageScore = normalizeValueToPercentage(
      benchmarkValue ??
        currentTestSet.info.defaultBenchmark ??
        DEFAULT_BENCHMARK_VALUE
    );

    const currentEntries = entriesGroupedBySide[side];
    currentEntries.forEach(entry => {
      const consentValue = getAverageScaleValue(consentScaleKeys, entry);
      dataAccumulator.averagePercentageScore += consentValue;
      dataAccumulator.totalCount += 1;

      const qualityValue = getAverageScaleValue(qualityScaleKeys, entry);
      dataAccumulator.implementationQualityPercentage += qualityValue;

      const importanceValue = getAverageScaleValue(importanceScaleKeys, entry);
      dataAccumulator.importancePercentage += importanceValue;

      dataAccumulator.consentItems.push(consentValue);
      dataAccumulator.qualityItems.push(qualityValue);
      dataAccumulator.importanceItems.push(importanceValue);
    });

    const totalCountAdjusted = dataAccumulator.totalCount || 1; // Prevent division by zero

    dataAccumulator.averagePercentageScore = normalizeValueToPercentage(
      dataAccumulator.averagePercentageScore / totalCountAdjusted
    );
    dataAccumulator.implementationQualityPercentage =
      normalizeValueToPercentage(
        dataAccumulator.implementationQualityPercentage / totalCountAdjusted
      );
    dataAccumulator.importancePercentage = normalizeValueToPercentage(
      dataAccumulator.importancePercentage / totalCountAdjusted
    );
  }

  dataAccumulator.aggregatedReport = getAggregatedItemsReport(
    currentTestId,
    side,
    testSetsByCubeSide,
    entriesGroupedBySide
  );

  return dataAccumulator;
}

export function filterEntriesByAttributes(
  entries: DtoEntry[],
  selectedFilters: SelectedFilters
) {
  return entries.filter(entry => {
    return Object.entries(selectedFilters).every(([key, values]) => {
      if (values.length === 0) {
        return true;
      }

      return values.includes(entry[key as keyof DtoEntry]);
    });
  });
}
